import { motion } from 'framer-motion';
import { 
  ShieldCheck, 
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  FileCheck,
  Users,
  BarChart3,
  ClipboardList,
  HeartPulse,
  Brain,
  Target,
  Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';

export const NR01 = ({ onDemoClick }) => {
  const features = [
    {
      icon: ClipboardList,
      title: 'Questionários Padronizados',
      description: 'Instrumentos validados para avaliação de riscos psicossociais no ambiente de trabalho.'
    },
    {
      icon: BarChart3,
      title: 'Dashboards de Análise',
      description: 'Visualização clara dos resultados por setor, cargo, unidade ou toda a empresa.'
    },
    {
      icon: FileCheck,
      title: 'Relatórios Estruturados',
      description: 'Documentação completa para auditorias e comprovação de conformidade.'
    },
    {
      icon: Target,
      title: 'Plano de Ação',
      description: 'Ferramenta para criar e acompanhar ações corretivas e preventivas.'
    },
    {
      icon: Users,
      title: 'Gestão de Participantes',
      description: 'Controle de aplicação, respostas e acompanhamento individual ou por grupos.'
    },
    {
      icon: HeartPulse,
      title: 'Indicadores de Saúde',
      description: 'Métricas relacionadas à saúde mental e bem-estar organizacional.'
    },
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Configuração',
      description: 'Configuramos a ferramenta com a estrutura da sua empresa (setores, cargos, unidades).'
    },
    {
      step: '02',
      title: 'Aplicação',
      description: 'Os colaboradores respondem ao questionário de forma anônima e confidencial.'
    },
    {
      step: '03',
      title: 'Análise',
      description: 'Os dados são processados e apresentados em dashboards e relatórios.'
    },
    {
      step: '04',
      title: 'Plano de Ação',
      description: 'Com base nos resultados, sua equipe define e acompanha as ações.'
    },
    {
      step: '05',
      title: 'Documentação',
      description: 'Relatórios prontos para apresentar em auditorias e fiscalizações.'
    },
  ];

  const faqs = [
    {
      question: 'O que é a NR-01 Psicossocial?',
      answer: 'A atualização da NR-01 incluiu os riscos psicossociais no gerenciamento de riscos ocupacionais (GRO). Isso significa que as empresas precisam identificar, avaliar e controlar fatores como estresse, assédio, carga mental excessiva e outros aspectos que afetam a saúde mental dos trabalhadores.'
    },
    {
      question: 'Todas as empresas precisam se adequar?',
      answer: 'Sim, a NR-01 se aplica a todas as empresas que possuem empregados regidos pela CLT. O nível de detalhamento da análise pode variar conforme o porte e grau de risco da empresa, mas todas precisam considerar os riscos psicossociais em seu PGR.'
    },
    {
      question: 'A ferramenta da CSP substitui o profissional de SST?',
      answer: 'Não. A ferramenta é um apoio para organizar o processo de coleta de dados, análise e documentação. A responsabilidade técnica pela avaliação e validação dos resultados permanece com o profissional habilitado (médico do trabalho, engenheiro de segurança, etc.).'
    },
    {
      question: 'As respostas dos colaboradores são confidenciais?',
      answer: 'Sim! O questionário é aplicado de forma anônima. Os resultados são apresentados de forma agregada, sem identificação individual. Isso é fundamental para garantir respostas honestas e proteger os colaboradores.'
    },
    {
      question: 'Qual o prazo para implementar?',
      answer: 'A ferramenta pode estar operacional em poucos dias. O prazo total depende da aplicação do questionário aos colaboradores e análise dos resultados. Recomendamos iniciar o quanto antes para ter tempo de implementar ações corretivas se necessário.'
    },
  ];

  return (
    <div data-testid="nr01-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-green-600 via-green-700 to-emerald-800 text-white overflow-hidden relative">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-300 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur text-sm font-medium mb-6">
                <ShieldCheck className="h-4 w-4" />
                NR-01 Psicossocial
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                Gestão de Riscos{' '}
                <span className="text-emerald-300">Psicossociais</span>{' '}
                Simplificada
              </h1>
              <p className="text-lg text-green-100 mb-8 max-w-xl">
                Ferramenta de apoio para organizar o processo de identificação, avaliação 
                e controle de riscos psicossociais conforme a NR-01.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={onDemoClick}
                  data-testid="nr01-hero-demo-btn"
                  className="bg-white text-green-700 hover:bg-green-50 px-8 py-6 rounded-full text-lg font-semibold"
                >
                  Falar com Especialista
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                <img
                  src="https://images.unsplash.com/photo-1646227655718-dd721b681d91?w=800&h=500&fit=crop"
                  alt="NR-01 Conformidade"
                  className="rounded-xl w-full"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-12 bg-amber-50 border-y border-amber-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Alert className="bg-white border-amber-300">
            <Info className="h-5 w-5 text-amber-600" />
            <AlertTitle className="text-amber-800 font-semibold">Importante</AlertTitle>
            <AlertDescription className="text-amber-700">
              A ferramenta CSP auxilia e organiza o processo de gestão de riscos psicossociais. 
              A validação final e responsabilidade técnica são do profissional habilitado da empresa 
              (médico do trabalho, engenheiro de segurança, psicólogo organizacional, etc.).
            </AlertDescription>
          </Alert>
        </div>
      </section>

      {/* What is NR-01 */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                O que são <span className="text-green-600">Riscos Psicossociais</span>?
              </h2>
              <p className="text-slate-600 mb-6">
                São fatores do ambiente de trabalho que podem afetar a saúde mental e o bem-estar 
                dos trabalhadores. A atualização da NR-01 passou a exigir que esses riscos sejam 
                incluídos no Programa de Gerenciamento de Riscos (PGR).
              </p>
              <div className="space-y-4">
                {[
                  { icon: Brain, text: 'Carga mental excessiva e estresse' },
                  { icon: Users, text: 'Assédio moral e sexual' },
                  { icon: AlertTriangle, text: 'Condições precárias de trabalho' },
                  { icon: HeartPulse, text: 'Falta de suporte organizacional' },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                      <item.icon className="h-5 w-5 text-green-600" />
                    </div>
                    <span className="text-slate-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="bg-slate-50 border-0 p-8">
                <CardContent className="p-0">
                  <h3 className="text-xl font-semibold text-slate-900 mb-4">
                    Sua empresa precisa:
                  </h3>
                  <ul className="space-y-3">
                    {[
                      'Identificar os riscos psicossociais presentes',
                      'Avaliar a gravidade e probabilidade',
                      'Implementar medidas de controle',
                      'Documentar todo o processo',
                      'Acompanhar a eficácia das ações',
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Recursos da <span className="text-green-600">Ferramenta</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Tudo que você precisa para organizar o processo de conformidade com a NR-01.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="h-full p-6 border border-slate-100 hover:border-green-200 hover:shadow-lg transition-all group bg-white">
                  <CardContent className="p-0">
                    <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center mb-4 group-hover:bg-green-100 transition-colors">
                      <feature.icon className="h-6 w-6 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Como <span className="text-green-600">Funciona</span>
            </h2>
          </motion.div>

          <div className="relative">
            {/* Timeline */}
            <div className="hidden lg:block absolute left-8 top-0 bottom-0 w-0.5 bg-green-200" />
            
            <div className="space-y-8">
              {howItWorks.map((item, index) => (
                <motion.div
                  key={index}
                  className="relative flex gap-8"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="hidden lg:flex flex-shrink-0 w-16 h-16 rounded-full bg-green-600 text-white items-center justify-center font-bold text-lg z-10">
                    {item.step}
                  </div>
                  <Card className="flex-1 p-6 border border-slate-100 hover:shadow-lg transition-shadow">
                    <CardContent className="p-0">
                      <div className="lg:hidden inline-flex w-10 h-10 rounded-full bg-green-600 text-white items-center justify-center font-bold text-sm mb-4">
                        {item.step}
                      </div>
                      <h3 className="text-xl font-semibold text-slate-900 mb-2">{item.title}</h3>
                      <p className="text-slate-600">{item.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Responsibility Notice */}
      <section className="py-20 md:py-32 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/20 mb-6">
              <AlertTriangle className="h-8 w-8 text-amber-400" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Responsabilidade Técnica
            </h2>
            <p className="text-lg text-slate-400 mb-8 max-w-3xl mx-auto">
              A ferramenta CSP é um apoio para organização do processo. A responsabilidade técnica 
              pela avaliação dos riscos psicossociais, validação dos resultados e assinatura dos 
              documentos oficiais permanece com o profissional habilitado da empresa 
              (médico do trabalho, engenheiro de segurança do trabalho, psicólogo organizacional, etc.).
            </p>
            <p className="text-slate-500">
              Consulte sempre seu profissional de SST ou jurídico para garantir a conformidade completa.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Perguntas <span className="text-green-600">Frequentes</span>
            </h2>
          </motion.div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-slate-50 rounded-xl border-0 px-6"
                data-testid={`nr01-faq-${index}`}
              >
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-green-600 to-green-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Comece a organizar sua conformidade com NR-01
            </h2>
            <p className="text-lg text-green-100 mb-8">
              Fale com um especialista e conheça como a ferramenta pode ajudar sua empresa.
            </p>
            <Button
              onClick={onDemoClick}
              data-testid="nr01-cta-demo-btn"
              className="bg-white text-green-700 hover:bg-green-50 px-8 py-6 rounded-full text-lg font-semibold"
            >
              Falar com Especialista NR-01
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
